import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { track } from '../lib/amplitude';
import { timeAgo } from '../lib/format';
import { qk } from '../queries/keys';
import { fetchInbox, fetchUnread, markRead, type InboxItem, type InboxPage } from '../queries/inbox';
import { AppLink, useGo } from './Nav';
import { BellIcon } from './icons';
import { cx } from './ui';

/**
 * The bell. A count of what has not been looked at, and under it the last
 * few things that happened to your recordings. Opening an item marks it
 * read and goes to the recording; the page at /inbox has all of them.
 *
 * The count is polled gently. A notification is minutes old before anyone
 * minds, and every tab of the app asking every few seconds would be the
 * loudest thing the API does.
 */

const EVENT_LABEL: Record<InboxItem['event'], string> = {
  first_view: 'First view',
  new_viewer: 'New viewer',
  reaction: 'Reaction',
  comment: 'Comment',
};

export function useUnread() {
  return useQuery({
    queryKey: qk.inboxUnread(),
    queryFn: fetchUnread,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
}

/** Marks read and refreshes the badge and both lists. */
export function useMarkRead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (ids?: string[]) => markRead(ids),
    onMutate: (ids) => {
      const now = new Date().toISOString();
      const flip = (page: InboxPage | undefined) =>
        page
          ? {
              ...page,
              unread: ids ? Math.max(0, page.unread - ids.length) : 0,
              items: page.items.map((item) => (!ids || ids.includes(item.id) ? { ...item, readAt: item.readAt ?? now } : item)),
            }
          : page;
      client.setQueriesData<InboxPage>({ queryKey: ['inbox'] }, flip);
      client.setQueryData<{ unread: number }>(qk.inboxUnread(), (current) =>
        current ? { unread: ids ? Math.max(0, current.unread - ids.length) : 0 } : current,
      );
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: qk.inboxUnread() });
      void client.invalidateQueries({ queryKey: ['inbox'] });
    },
  });
}

export function InboxRow({ item, onOpen, compact = false }: { item: InboxItem; onOpen: (item: InboxItem) => void; compact?: boolean }) {
  const unread = item.readAt === null;
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      className={cx(
        'flex w-full items-start gap-3 rounded px-2.5 py-2 text-left transition-colors duration-120 hover:bg-slate-800',
        compact ? '' : 'border-b border-hairline last:border-b-0 py-3',
      )}
    >
      <span
        aria-hidden
        className={cx('mt-2 h-1.5 w-1.5 shrink-0 rounded-full', unread ? 'bg-signal' : 'bg-transparent')}
      />
      <span className="min-w-0 flex-1">
        <span className={cx('block truncate text-body', unread ? 'text-chalk' : 'text-chalk-dim')}>{item.body}</span>
        <span className="mt-0.5 flex items-center gap-2 text-caption text-chalk-faint">
          <span>{EVENT_LABEL[item.event] ?? item.event}</span>
          <span>·</span>
          <span title={item.createdAt}>{timeAgo(item.createdAt)}</span>
          {item.videoGone ? <span>· recording removed</span> : null}
        </span>
      </span>
      {unread ? <span className="sr-only">Unread</span> : null}
    </button>
  );
}

export function InboxMenu() {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const go = useGo();
  const unread = useUnread();
  const recent = useQuery({ queryKey: [...qk.inbox(), 'recent'], queryFn: () => fetchInbox({ limit: 8 }), enabled: open, staleTime: 10_000 });
  const read = useMarkRead();
  const count = unread.data?.unread ?? 0;

  useEffect(() => {
    if (!open) return;
    track('Inbox Opened', { unread: count });
    const onDown = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
    // The count at the moment of opening is the one worth recording.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const openItem = (item: InboxItem) => {
    track('Inbox Item Opened', { event: item.event });
    if (item.readAt === null) read.mutate([item.id]);
    setOpen(false);
    if (!item.videoGone) go(item.url);
  };

  return (
    <div className="relative" ref={root}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={count > 0 ? `Inbox, ${count} unread` : 'Inbox'}
        title="Inbox"
        className="relative flex h-8 w-8 items-center justify-center rounded-full border border-hairline bg-slate-800 text-chalk-dim transition-colors duration-120 hover:border-slate-600 hover:text-chalk"
      >
        <BellIcon size={15} />
        {count > 0 ? (
          <span className="tnum absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-signal px-1 text-[10px] font-medium leading-none text-slate-950">
            {count > 99 ? '99+' : count}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Inbox"
          className="absolute right-0 z-30 mt-1.5 w-80 animate-fade-up rounded-md border border-hairline bg-slate-850 p-1 shadow-raised"
        >
          <div className="flex items-center justify-between px-2.5 py-2">
            <p className="text-body text-chalk">Inbox</p>
            {count > 0 ? (
              <button type="button" onClick={() => read.mutate(undefined)} className="text-caption text-chalk-dim hover:text-chalk">
                Mark all read
              </button>
            ) : null}
          </div>
          <div className="my-1 h-px bg-hairline" />
          {recent.isPending ? (
            <p className="px-2.5 py-3 text-caption text-chalk-faint">Loading</p>
          ) : recent.isError || !recent.data ? (
            <p className="px-2.5 py-3 text-caption text-chalk-faint">The inbox did not load.</p>
          ) : recent.data.items.length === 0 ? (
            <p className="px-2.5 py-3 text-caption text-chalk-faint">Nothing yet. When somebody watches, reacts or comments, it lands here.</p>
          ) : (
            <div className="max-h-96 overflow-auto">
              {recent.data.items.map((item) => (
                <InboxRow key={item.id} item={item} onOpen={openItem} compact />
              ))}
            </div>
          )}
          <div className="my-1 h-px bg-hairline" />
          <AppLink to="/inbox" onClick={() => setOpen(false)} className="block rounded px-2.5 py-2 text-caption text-chalk-dim hover:bg-slate-800 hover:text-chalk">
            See everything
          </AppLink>
        </div>
      ) : null}
    </div>
  );
}
